// Public supplier signup endpoints are mounted at /api directly on the backend
// (not under /api/organizations/:orgSlug). The default axios instance has interceptors
// installed by axiosHelper.js that rewrite /api/* to /api/organizations/:orgSlug/*.
// To bypass that rewriting, we create a separate axios instance without those interceptors.
import axios from 'axios';

const publicAxios = axios.create();

interface CheckOrgResponse {
  companyName: string | null;
  address: string;
  city: string;
  postalCode: string;
  exists: boolean;
  admins: Array<{ name: string; email: string }>;
}

export interface Category {
  _id: string;
  name: string;
  subAreas: string[];
}

export interface Colleague {
  name: string;
  email: string;
  title: string;
  phoneNumber: string;
  isAdmin: boolean;
  isContact: boolean;
}

interface CreateAccountData {
  organizationNumber: string;
  companyName: string;
  address: string;
  city: string;
  postalCode: string;
  termsAcceptedAt: string;
  areaIds: string[];
  places: string[];
  name: string;
  title: string;
  phoneNumber: string;
  email: string;
  password: string;
  addAsContact: boolean;
  colleagues: Colleague[];
  verificationCode: string;
  sessionId: string;
}

interface CreateAccountResponse {
  userData: {
    accessToken: string;
    id: string;
    role: string;
    name: string;
    email: string;
    phone: string;
    orgSlug: string;
    isActive: boolean;
  };
  organizationData: Array<{
    _id: string;
    slug: string;
    name: string;
  }>;
  orgSlug: string;
}

type LinkExistingAccountData = Omit<CreateAccountData, 'password' | 'verificationCode'>;

interface LinkedAccountResponse {
  linked: true;
  orgSlug: string;
}

class ServiceSupplierSignupController {
  private baseUrl = '/api';

  async checkOrg(organizationNumber: string): Promise<CheckOrgResponse> {
    const response = await publicAxios.post(`${this.baseUrl}/supplier-signup/check-org`, {
      organizationNumber,
    });
    return response.data;
  }
  
  async checkEmail(email: string): Promise<{ exists: boolean }> {
    const response = await publicAxios.post(`${this.baseUrl}/supplier-signup/check-email`, { email });
    return response.data;
  }

  async getCategories(): Promise<Category[]> {
    const response = await publicAxios.get(`${this.baseUrl}/supplier-signup/categories`);
    return response.data;
  }

  async getTopCategories(limit = 6, places?: string[]): Promise<{ areaId: string; name: string; count: number }[]> {
    const response = await publicAxios.get(`${this.baseUrl}/supplier-signup/top-categories`, {
      params: { limit, places: places?.join(',') },
    });
    return response.data;
  }

  async getProcurementStats(areaIds: string[], places: string[]): Promise<{ ongoingCount: number; marketplaceCount: number; recentCount: number }> {
    const response = await publicAxios.get(
      `${this.baseUrl}/supplier-signup/procurement-stats`,
      { params: { areaIds: areaIds.join(','), places: places.join(',') } }
    );
    return response.data;
  }

  async sendVerification(email: string): Promise<{ success: boolean; emailSent?: boolean }> {
    const response = await publicAxios.post(`${this.baseUrl}/supplier-signup/send-verification`, { email });
    return response.data;
  }

  async verifyAndCreate(data: CreateAccountData | LinkExistingAccountData): Promise<CreateAccountResponse | LinkedAccountResponse> {
    const response = await publicAxios.post(`${this.baseUrl}/supplier-signup/verify-and-create`, data);
    return response.data;
  }
}

export const ServiceSupplierSignup = new ServiceSupplierSignupController();
